import { ApiResponse } from '@ustra/data/src/models/api-model'
import { HttpMethod } from '@ustra/core/src/server/http/const'
import { UtraService } from '@ustra/nuxt/src/services/ustra-service'
import { LoginResult, UserInfo, UserCertReqn, DviceCertResult, UserCertCondition } from '../models/login-model'
import { UserIdInfo, UserIdCondition } from '../models/user-id-model'
import { UserChgPwCondition, UserChgPw } from '../models/user-chg-pw-model'

export class LoginService extends UtraService {
  /**
   * 로그인
   * @param userId 사용자아이디
   * @param userPw 사용자 패스워드
   */
  async login(userId: string, userPw: string) {
    const result = await this.$ustra.api.call<ApiResponse<LoginResult>>({
      url: '/api/auth/login',
      method: HttpMethod.POST,
      passOnResponseError: true,
      data: {
        userId,
        userPw,
        // encAccount: encryptAes256(JSON.stringify({ userId, userPw }), process.env.SERVER_PROP_ENC_KEY),
        // authSysCd: this.$ustra.store.config().appProp.serverAppProp.systemCode,
      },
    })

    return result
  }

  /**
   * 자동 로그인
   *
   * @param {string} loginToken 로그인 토큰
   * @returns
   * @memberof LoginService
   */
  async autoLogin(loginToken: string) {
    const result = await this.$ustra.api.call<ApiResponse<LoginResult>>({
      url: '/api/auth/login',
      method: HttpMethod.POST,
      passOnResponseError: true,
      headers: { 'hdhs-rtoken': loginToken },
      data: {},
    })

    return result
  }

  /**
   * 점유 인증 확인
   *
   * @param {string} userId
   * @param {string} dvicId
   * @returns
   * @memberof LoginService
   */
  async isDivcCert(userId: string, dvicId: string) {
    const result = await this.$ustra.api.call<ApiResponse<DviceCertResult>>({
      url: '/api/login/isDvicCert',
      method: HttpMethod.POST,
      passOnResponseError: true,
      data: { userId, dvicId },
    })

    return result.data.body
  }

  /**
   * 점유 인증 등록
   *
   * @param {UserCertCondition} userCert
   * @returns
   * @memberof LoginService
   */
  async insertDvicCert(userCert: UserCertCondition) {
    const result = await this.$ustra.api.call<ApiResponse<any>>({
      url: '/api/login/insertDivcCert',
      method: HttpMethod.POST,
      data: userCert,
    })

    return result.data.body
  }

  /**
   * 접속자 확인
   *
   * @param {string} userId
   * @param {string} dvicId
   * @returns
   * @memberof LoginService
   */
  async isConnected() {
    if (!this.$ustra.hs.store.cmData.appProp.checkDuplication) return { msgCd: '0000' }

    const result = await this.$ustra.api.call<ApiResponse<DviceCertResult>>({
      url: '/api/login/isConnected',
      method: HttpMethod.POST,
      passOnResponseError: true,
      data: {},
    })

    return result.data.body
  }

  /**
   * 2차 인증
   *
   * @param {string} userId
   * @param {string} authenticationKey
   * @returns
   * @memberof LoginService
   */
  async login2nd(userId: string, authenticationKey: string) {
    const result = await this.$ustra.api.call<ApiResponse<LoginResult>>({
      url: '/api/auth/login/key',
      method: HttpMethod.POST,
      passOnResponseError: true,
      data: {
        userId,
        authenticationKey,
        // encAccount: encryptAes256(JSON.stringify({ userId, userPw }), process.env.SERVER_PROP_ENC_KEY),
        // authSysCd: this.$ustra.store.config().appProp.serverAppProp.systemCode,
      },
    })

    return result
  }

  /**
   * 로그아웃
   *
   * @returns
   * @memberof LoginService
   */
  async logout() {
    const result = await this.$ustra.api.call<ApiResponse<string>>({
      url: `/api/auth/logout`,
      method: HttpMethod.POST,
      passOnResponseError: true,
      data: {},
    })

    return result.data.body
  }

  /**
   * 유저의 SMS, Email 정보 조회
   *
   * @param {string} userId
   * @returns
   * @memberof LoginService
   */
  async selectUserInfo() {
    const result = await this.$ustra.api.call<ApiResponse<UserInfo>>({
      url: '/api/login/selectUserInfo',
      method: HttpMethod.POST,
      data: {},
    })

    return result.data
  }

  /**
   * 인증 번호 요청
   *
   * @param {UserCertCondition} userCert
   * @returns
   * @memberof LoginService
   */
  async reqCertNo(userCert: UserCertCondition) {
    const result = await this.$ustra.api.call<ApiResponse<UserCertReqn>>({
      url: '/api/login/selectCertReqn',
      method: HttpMethod.POST,
      data: userCert,
    })

    return result.data
  }

  /**
   * 인증 번호 확인
   *
   * @param {UserCertCondition} userCert
   * @returns
   * @memberof LoginService
   */
  async checkCertNo(userCert: UserCertCondition) {
    const result = await this.$ustra.api.call<ApiResponse<any>>({
      url: '/api/login/selectCertConfirm',
      method: HttpMethod.POST,
      data: userCert,
    })

    return result.data.body
  }

  /**
   * 패스워드 변경 처리 (로그인 전)
   * @param $core
   * @param usrId 아이디
   * @param oldPwd 패스워드
   * @param newPwd 신규 패스워드
   */
  async updatePassword(usrId: string, oldPwd: string, newPwd: string) {
    const result = await this.$ustra.api.call<ApiResponse<string>>({
      url: `/api/auth/edit-password`,
      method: HttpMethod.POST,
      data: { usrId, oldPwd, newPwd },
    })

    return result.data
  }

  /**
   * 아이디 찾기
   *
   * @param {UserIdCondition} idCondition
   * @returns
   * @memberof LoginService
   */
  async selectUserIdInfo(idCondition: UserIdCondition) {
    const result = await this.$ustra.api.call<ApiResponse<UserIdInfo>>({
      url: '/api/login/selectUserIdInfo',
      method: HttpMethod.POST,
      data: idCondition,
    })

    return result.data.body
  }

  /**
   * 패스워드 초기화
   *
   * @param {UserIdCondition} idCondition
   * @returns
   * @memberof LoginService
   */
  async updateUserPwdInit(idCondition: UserIdCondition) {
    const result = await this.$ustra.api.call<ApiResponse<UserIdInfo>>({
      url: '/api/login/updateUserPwdInit',
      method: HttpMethod.POST,
      data: idCondition,
    })

    return result.data.body
  }

  async updateUserPwdChg(conditoin: UserChgPwCondition) {
    const result = await this.$ustra.api.call<ApiResponse<UserChgPw>>({
      url: '/api/login/updateUserPwdChg',
      method: HttpMethod.POST,
      data: conditoin,
    })

    return result.data.body
  }

  /**
   * 메뉴 이력 추가
   *
   * @param {string} userId
   * @param {string} menuId
   * @returns
   * @memberof LoginService
   */
  async insertMenuHist(menuId: string) {
    const result = await this.$ustra.api.call<ApiResponse<any>>({
      url: '/api/login/insertMenuHist',
      method: HttpMethod.POST,
      data: {
        menuId,
      },
    })

    return result.data.body
  }
}
export const loginService = new LoginService()
export default loginService
